import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import type { Menu, MenuItem, Recipe } from '@ricettario/shared';
import { api, ApiError } from '../api/client';
import { useMenu } from '../hooks/useMenus';
import { useRecipes } from '../hooks/useRecipes';

export default function MenuDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: menu, loading, error, refresh } = useMenu(id);
  const [showAdd, setShowAdd] = useState(false);
  const [savingName, setSavingName] = useState(false);

  async function saveName(name: string) {
    if (!menu || savingName) return;
    setSavingName(true);
    try {
      await api.patch(`/api/v1/menus/${menu.id}`, { name: name.trim() || null });
      refresh();
    } catch (err) {
      alert(err instanceof ApiError ? err.message : 'errore salvataggio');
    } finally {
      setSavingName(false);
    }
  }

  async function deleteMenu() {
    if (!menu) return;
    if (!confirm('Eliminare questo menu?')) return;
    try {
      await api.delete(`/api/v1/menus/${menu.id}`);
      navigate('/menus');
    } catch (err) {
      alert(err instanceof ApiError ? err.message : 'errore eliminazione');
    }
  }

  async function duplicateMenu() {
    if (!menu) return;
    try {
      const dup = await api.post<Menu>(`/api/v1/menus/${menu.id}/duplicate`);
      navigate(`/menu/${dup.id}`);
    } catch (err) {
      alert(err instanceof ApiError ? err.message : 'errore duplicazione');
    }
  }

  if (loading) {
    return (
      <main className="max-w-xl mx-auto px-5 py-6 space-y-3">
        <div className="skeleton h-5 w-20" />
        <div className="skeleton h-10 w-1/2" />
        <div className="skeleton h-24 w-full mt-6" />
      </main>
    );
  }
  if (error || !menu) {
    return (
      <main className="max-w-xl mx-auto px-5 py-20 text-center space-y-5">
        <p className="text-brand text-lg font-bold">{error ?? 'Menu non trovato.'}</p>
        <Link to="/menus" className="btn-soft">← torna ai menu</Link>
      </main>
    );
  }

  const itemsCount = menu.items.length;

  return (
    <main className="max-w-xl mx-auto px-5 pt-5 pb-32">
      <nav className="mb-4 flex items-center justify-between">
        <Link to="/menus" className="btn-ghost">← menu</Link>
        <div className="flex items-center gap-1">
          <button onClick={duplicateMenu} className="btn-ghost text-xs" title="Duplica">
            ⧉ duplica
          </button>
          <button onClick={deleteMenu} className="btn-ghost text-xs" title="Elimina">
            elimina
          </button>
        </div>
      </nav>

      <header className="mb-6 animate-riseIn">
        <EditableName initial={menu.name ?? ''} onSave={saveName} placeholder="Nome menu (opzionale)" />
        <div className="text-sm text-muted mt-2">
          {new Date(menu.createdAt).toLocaleDateString('it-IT', {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
          })}
          {menu.sentAt && (
            <>
              {' · '}
              <span className="text-sage font-semibold">inviato a Bring</span>
            </>
          )}
        </div>
      </header>

      {itemsCount === 0 ? (
        <div className="card py-12 text-center animate-riseIn">
          <div
            className="mx-auto mb-4 h-16 w-16 rounded-3xl flex items-center justify-center text-4xl"
            style={{ background: 'linear-gradient(135deg,#FFE8D4,#FFD4C2)' }}
            aria-hidden
          >
            📖
          </div>
          <p className="text-base font-bold text-ink mb-1">Menu vuoto</p>
          <p className="text-sm text-muted px-8 mb-5">Aggiungi le ricette che cucinerai questa settimana.</p>
          <button className="btn-brand" onClick={() => setShowAdd(true)}>
            + Aggiungi ricetta
          </button>
        </div>
      ) : (
        <>
          <ul className="flex flex-col gap-2.5 mb-4 animate-riseIn">
            {menu.items.map((item) => (
              <MenuItemRow key={item.id} item={item} menuId={menu.id} onChange={refresh} />
            ))}
          </ul>
          <button
            onClick={() => setShowAdd(true)}
            className="btn-soft w-full py-3 mb-6"
          >
            + Aggiungi ricetta
          </button>
        </>
      )}

      {itemsCount > 0 && (
        <div className="bottom-bar">
          <div className="max-w-xl mx-auto px-5">
            <Link to={`/menu/${menu.id}/summary`} className="btn-brand w-full py-4">
              Riepilogo ingredienti →
            </Link>
          </div>
        </div>
      )}

      {showAdd && (
        <AddRecipeModal
          menuId={menu.id}
          onClose={() => setShowAdd(false)}
          onAdded={() => {
            setShowAdd(false);
            refresh();
          }}
        />
      )}
    </main>
  );
}

function EditableName({
  initial,
  onSave,
  placeholder,
}: {
  initial: string;
  onSave: (v: string) => void;
  placeholder: string;
}) {
  const [value, setValue] = useState(initial);
  useEffect(() => setValue(initial), [initial]);

  return (
    <input
      type="text"
      value={value}
      onChange={(e) => setValue(e.target.value)}
      onBlur={() => {
        if (value !== initial) onSave(value);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
      }}
      placeholder={placeholder}
      className="input-line text-[clamp(22px,6vw,28px)] font-extrabold tracking-tightest leading-tight"
      maxLength={120}
    />
  );
}

function MenuItemRow({
  item,
  menuId,
  onChange,
}: {
  item: MenuItem;
  menuId: string;
  onChange: () => void;
}) {
  const [servings, setServings] = useState(item.servings);
  const [pending, setPending] = useState(false);

  useEffect(() => setServings(item.servings), [item.servings]);

  async function changeServings(next: number) {
    const clamped = Math.max(1, Math.min(50, next));
    setServings(clamped);
    if (clamped === item.servings) return;
    setPending(true);
    try {
      await api.patch(`/api/v1/menus/${menuId}/items/${item.id}`, { servings: clamped });
      onChange();
    } catch (err) {
      alert(err instanceof ApiError ? err.message : 'errore');
      setServings(item.servings);
    } finally {
      setPending(false);
    }
  }

  async function remove() {
    if (!confirm(`Togliere "${item.recipe.name}" dal menu?`)) return;
    try {
      await api.delete(`/api/v1/menus/${menuId}/items/${item.id}`);
      onChange();
    } catch (err) {
      alert(err instanceof ApiError ? err.message : 'errore');
    }
  }

  return (
    <li className="card p-3 flex items-center gap-3">
      <Link
        to={`/recipe/${item.recipe.id}`}
        className="h-12 w-12 rounded-xl flex items-center justify-center text-2xl shrink-0 bg-surface"
        aria-label={`apri ${item.recipe.name}`}
      >
        {item.recipe.icon || '🍽️'}
      </Link>
      <div className="flex-1 min-w-0">
        <Link to={`/recipe/${item.recipe.id}`} className="block">
          <p className="font-bold text-[14.5px] leading-[1.2] line-clamp-2 tracking-tightest">
            {item.recipe.name}
          </p>
        </Link>
        <div className="flex items-center gap-1.5 mt-1.5">
          <button
            onClick={() => changeServings(servings - 1)}
            disabled={pending || servings <= 1}
            className="h-7 w-7 rounded-full bg-surface hover:bg-line disabled:opacity-30 text-sm font-bold"
            aria-label="−"
          >
            −
          </button>
          <span className="font-bold text-sm w-8 text-center tabular-nums">{servings}</span>
          <button
            onClick={() => changeServings(servings + 1)}
            disabled={pending || servings >= 50}
            className="h-7 w-7 rounded-full bg-surface hover:bg-line disabled:opacity-30 text-sm font-bold"
            aria-label="+"
          >
            +
          </button>
          <span className="text-xs text-muted ml-1">person{servings === 1 ? 'a' : 'e'}</span>
        </div>
      </div>
      <button
        onClick={remove}
        className="btn-ghost text-xs shrink-0 !p-2"
        aria-label="rimuovi"
      >
        <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
          <path d="M18 6L6 18M6 6l12 12" />
        </svg>
      </button>
    </li>
  );
}

function AddRecipeModal({
  menuId,
  onClose,
  onAdded,
}: {
  menuId: string;
  onClose: () => void;
  onAdded: () => void;
}) {
  const [search, setSearch] = useState('');
  const { data: recipes, loading } = useRecipes(search, null);
  const [pickedRecipe, setPickedRecipe] = useState<Recipe | null>(null);
  const [servings, setServings] = useState(2);
  const [saving, setSaving] = useState(false);

  const list = useMemo(() => recipes ?? [], [recipes]);

  function pick(r: Recipe) {
    setPickedRecipe(r);
    setServings(r.baseServings);
  }

  async function confirmAdd() {
    if (!pickedRecipe || saving) return;
    setSaving(true);
    try {
      await api.post(`/api/v1/menus/${menuId}/items`, {
        recipeId: pickedRecipe.id,
        servings,
      });
      onAdded();
    } catch (err) {
      alert(err instanceof ApiError ? err.message : 'errore');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-ink/50 animate-riseIn">
      <div
        className="bg-bg rounded-t-[28px] sm:rounded-[28px] w-full sm:max-w-md max-h-[85vh] flex flex-col shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="px-5 pt-5 pb-3 flex items-center justify-between">
          <h2 className="font-extrabold text-xl tracking-tightest">
            {pickedRecipe ? 'Quante porzioni?' : 'Aggiungi ricetta'}
          </h2>
          <button onClick={onClose} className="btn-ghost !p-2" aria-label="chiudi">
            <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </header>

        {pickedRecipe ? (
          <div className="px-5 pb-6 flex flex-col gap-5">
            <div className="card p-4 flex items-center gap-3">
              <div className="h-12 w-12 rounded-xl flex items-center justify-center text-2xl bg-surface shrink-0">
                {pickedRecipe.icon || '🍽️'}
              </div>
              <p className="font-bold text-[15px] leading-tight">{pickedRecipe.name}</p>
            </div>

            <div className="card p-5 flex items-center justify-between">
              <span className="text-sm font-semibold">Porzioni nel menu</span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setServings((s) => Math.max(1, s - 1))}
                  className="h-10 w-10 rounded-full bg-surface hover:bg-line disabled:opacity-30 text-lg font-bold"
                  disabled={servings <= 1}
                  aria-label="−"
                >
                  −
                </button>
                <span className="font-bold text-2xl w-10 text-center tabular-nums">{servings}</span>
                <button
                  onClick={() => setServings((s) => Math.min(50, s + 1))}
                  className="h-10 w-10 rounded-full bg-surface hover:bg-line disabled:opacity-30 text-lg font-bold"
                  disabled={servings >= 50}
                  aria-label="+"
                >
                  +
                </button>
              </div>
            </div>

            <div className="flex gap-2">
              <button onClick={() => setPickedRecipe(null)} className="btn-soft flex-1">
                ← indietro
              </button>
              <button onClick={confirmAdd} disabled={saving} className="btn-brand flex-1">
                {saving ? 'aggiungo…' : 'Aggiungi'}
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="px-5 pb-3">
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="cerca una ricetta…"
                className="input-bring"
                autoFocus
              />
            </div>
            <ul className="flex-1 overflow-y-auto px-5 pb-6 flex flex-col gap-2">
              {loading && <li className="skeleton h-16 w-full" />}
              {!loading && list.length === 0 && (
                <li className="text-center text-muted py-8">
                  {search ? 'nessuna ricetta trovata' : 'nessuna ricetta ancora'}
                </li>
              )}
              {list.map((r) => (
                <li key={r.id}>
                  <button
                    onClick={() => pick(r)}
                    className="card p-3 w-full flex items-center gap-3 text-left active:scale-[0.99] transition"
                  >
                    <div className="h-11 w-11 rounded-xl flex items-center justify-center text-xl bg-surface shrink-0">
                      {r.icon || '🍽️'}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-[14px] leading-tight truncate">{r.name}</p>
                      <p className="text-xs text-muted mt-0.5">
                        base {r.baseServings} person{r.baseServings === 1 ? 'a' : 'e'}
                      </p>
                    </div>
                    <span className="text-brand font-bold text-lg">+</span>
                  </button>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    </div>
  );
}
